import { usePremiumInfoUrl } from '@src/utils/premiumReturn';
import {
  NotificationsBell,
  NotificationsMenu,
} from '@src/components/Notifications/NotificationsMenu';
import { useNotifications } from '@src/components/Notifications/NotificationsContext';
import { RefreshIcon } from '../icons/RefreshIcon';
import { useMobileRefresh } from '../MobileRefresh/useMobileRefresh';
import { CrownIcon } from '@src/components/icons/CrownIcon';
import { InlineAds } from '@src/components/Ads/Ads';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountChip } from '@src/components/AccountChip/AccountChip';
import { AccountSwitcherHost } from '@src/components/AccountSwitcher/AccountSwitcherHost';
import { AppHeader } from '@src/components/AppHeader/AppHeader';
import { Brand } from '@src/components/Brand/Brand';
import { Button } from '@src/components/Button/Button';
import type { ContainerWidth } from '@src/components/Container/Container';
import { Footer } from '@src/components/Footer/Footer';
import { GiftIcon } from '@src/components/icons/GiftIcon';
import { HomeIcon } from '@src/components/icons/HomeIcon';
import { PlusIcon } from '@src/components/icons/PlusIcon';
import { HelpIcon } from '@src/components/icons/HelpIcon';
import { UsersIcon } from '@src/components/icons/UsersIcon';
import { Link } from '@src/components/Link/Link';
import { Page, type PageProps } from '@src/components/Page/Page';

import {
  ADMIN_PATH,
  ACCOUNT_BOXES_PATH,
  ACCOUNT_PROFILES_PATH,
  LOGIN_PATH,
} from '@src/routing/routes';
import { guestOnlyGame, sessionBoxes } from '@src/state/guestGame';
import { useAppSelector } from '@src/state/hooks';
import './Shell.css';
import { mainSiteUrl, premiumGameUrl } from '@src/routing/boxDomainNavigation';

const HEADER_LINKS = [
  { href: '/', labelKey: 'header.home', icon: <HomeIcon /> },
  { href: '/box/new', labelKey: 'footer.createGame', icon: <PlusIcon /> },
  { href: '/randomizer', labelKey: 'footer.quickDraw', icon: <GiftIcon /> },
  // { href: '/gifts', labelKey: 'gifts.title', icon: <GiftIcon /> },
  { href: '/faq', labelKey: 'footer.faq', icon: <HelpIcon /> },
] as const;

export interface ShellProps {
  landscape?: PageProps['landscape'];
  landscapeForeground?: ReactNode;
  landscapeOverlay?: ReactNode;
  children: ReactNode;
  /** Full-bleed band under the header — the home sky. */
  banner?: ReactNode;
  /** Stays on the bar — home pins create here after the page CTA scrolls away. */
  headerCta?: ReactNode;
  /** Optional replacement for the header controls, used by read-only previews. */
  headerTools?: ReactNode;
  /** Header and footer column. Defaults to the standard page width (`content`). */
  width?: ContainerWidth;
  footer?: boolean;
  /** Product bar at the top. Off for focused flows like a card claim. */
  header?: boolean;
  /** Let artwork remain visible beneath the desktop toolbar. */
  transparentHeader?: boolean;
  stickyDesktopHeader?: boolean;
}

/** Product chrome: header, the page body, inverse footer. */
export function Shell({
  children,
  landscape = 'soft',
  landscapeForeground,
  landscapeOverlay,
  banner,
  headerCta,
  headerTools,
  width = 'content',
  footer = true,
  header = true,
  transparentHeader = false,
  stickyDesktopHeader = false,
}: ShellProps) {
  const premiumInfoHref = usePremiumInfoUrl();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const mobileRefresh = useMobileRefresh(location.pathname + location.search);
  const user = useAppSelector((state) => state.session.user);
  const gameDomain = typeof window !== 'undefined' && Boolean(window.__ssBoxKey);

  function signUp(testId: string) {
    return (
      <Button
        variant="secondary"
        size="small"
        testId={testId}
        onClick={() => {
          navigate(LOGIN_PATH);
        }}
      >
        {t('header.signUp')}
      </Button>
    );
  }

  const account = user ? <AccountSwitcherHost session={user} /> : null;

  // Only a guest with exactly one saved game gets a direct game shortcut.
  const { unread } = useNotifications();
  const boxes = sessionBoxes(user);
  const onlyGame = guestOnlyGame(user);
  const gameLabel = onlyGame?.isArchived
    ? t('header.archivedGame', { name: onlyGame.name })
    : onlyGame?.name;
  const gameChip = onlyGame ? (
    <AccountChip
      icon={<GiftIcon />}
      href={premiumGameUrl(onlyGame)}
      labelHidden
      label={gameLabel ?? onlyGame.name}
      testId="header-game"
    >
      {gameLabel}
    </AccountChip>
  ) : null;

  const myGames =
    user && !user.isGuest && boxes.length ? (
      <AccountChip
        icon={<GiftIcon />}
        href={ACCOUNT_BOXES_PATH}
        labelHidden
        label={t('header.myGames')}
        testId="header-boxes"
      >
        {t('header.myGames')}
      </AccountChip>
    ) : null;

  return (
    <Page landscape={landscape} landscapeForeground={landscapeForeground} landscapeOverlay={landscapeOverlay}>
      <div className="shell__stage">
        {header ? (
          <AppHeader
            key={location.pathname}
            className={[transparentHeader && 'app-header--transparent-desktop', !stickyDesktopHeader && 'app-header--scroll-away-desktop'].filter(Boolean).join(' ') || undefined}
            testId="app-header"
            width={width}
            menuLabel={
              unread
                ? `${t('header.menu')}. ${t('notifications.withUnread', { count: unread })}`
                : t('header.menu')
            }
            menuUnread={unread > 0}
            menuTitle={t('header.menuTitle')}
            closeLabel={t('header.close')}
            drawerOnly
            actions={
              <nav className="shell__menu" aria-label={t('header.menuTitle')}>
                <div className="shell__menu-links">
                  {gameDomain ? (
                    <Link href={mainSiteUrl('/')} testId="header-back-to-site">
                      <HomeIcon />
                      <span>{t('header.backToSite')}</span>
                    </Link>
                  ) : (
                    HEADER_LINKS.map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        aria-current={location.pathname === link.href ? 'page' : undefined}
                      >
                        {link.icon}
                        <span>{t(link.labelKey)}</span>
                      </Link>
                    ))
                  )}
                  {<Link href={premiumInfoHref} testId="header-premium-menu"><CrownIcon />{t('footer.premium')}</Link>}
                  {!gameDomain && (user ? (
                    <Link href={ACCOUNT_PROFILES_PATH} testId="header-profiles-menu">
                      <UsersIcon />
                      <span>{t('header.myProfiles')}</span>
                    </Link>
                  ) : (
                    signUp('header-signup-menu')
                  ))}
                  {user && (
                    <Link
                      href={gameDomain ? '/notifications' : '/account/notifications'}
                      testId="header-notifications-menu"
                    >
                      <NotificationsBell />
                      <span>{t('notifications.title')}</span>
                    </Link>
                  )}
                  {user && !user.isGuest && (user.accountRole === 'super_admin' || /^goods_admin_.+/.test(user.accountRole || '')) && (
                    <Link href={gameDomain ? mainSiteUrl(ADMIN_PATH) : ADMIN_PATH} testId="header-admin-menu">
                      <UsersIcon />
                      <span>{t('admin.title')}</span>
                    </Link>
                  )}
                  {headerCta}
                </div>
                {!gameDomain && (onlyGame || myGames) ? (
                  <section
                    className="shell__menu-game"
                    aria-label={t(onlyGame ? 'header.myGame' : 'header.myGames')}
                  >
                    <span className="shell__menu-game-label">
                      {t(onlyGame ? 'header.myGame' : 'header.myGames')}
                    </span>
                    <Link
                      href={onlyGame ? premiumGameUrl(onlyGame) : ACCOUNT_BOXES_PATH}
                      testId={onlyGame ? 'header-game-menu' : 'header-boxes-menu'}
                    >
                      <GiftIcon />
                      <span>{onlyGame ? gameLabel : t('header.myGames')}</span>
                    </Link>
                  </section>
                ) : null}
                {mobileRefresh.enabled && (
                  <Button
                    variant="ghost"
                    className="shell__menu-refresh"
                    icon={<RefreshIcon />}
                    disabled={mobileRefresh.blocked}
                    onClick={mobileRefresh.refresh}
                  >
                    {t('header.refresh')}
                  </Button>
                )}
              </nav>
            }
            tools={
              headerTools ?? (
                <>
                  {<span className="shell__premium-link"><Link href={premiumInfoHref} testId="header-premium"><CrownIcon />{t('footer.premium')}</Link></span>}
                  {!gameDomain && gameChip}
                  {!gameDomain && myGames}
                  {headerCta}
                  {user && <NotificationsMenu />}
                  {!gameDomain && (account ?? (
                    <span className="app-header__tool app-header__tool--defer-signup">
                      {signUp('header-signup')}
                    </span>
                  ))}
                </>
              )
            }
          >
            <Brand href={mainSiteUrl('/')} testId="brand">
              {t('brand.name')}
            </Brand>
          </AppHeader>
        ) : null}
        {mobileRefresh.enabled && (mobileRefresh.distance > 10 || mobileRefresh.refreshing) && (
          <div className="shell__refresh" role="status" aria-label={t('header.refresh')}>
            <span
              className={
                mobileRefresh.refreshing
                  ? 'shell__refresh-icon shell__refresh-icon--spinning'
                  : 'shell__refresh-icon'
              }
              style={
                mobileRefresh.refreshing
                  ? undefined
                  : { transform: `rotate(${mobileRefresh.distance * 2.5}deg)` }
              }
            >
              <RefreshIcon />
            </span>
          </div>
        )}
        <div
          className={`shell__content${mobileRefresh.enabled ? ' shell__content--refreshable' : ''}${
            mobileRefresh.distance > 0 ? ' shell__content--pulling' : ''
          }`}
          style={
            mobileRefresh.enabled
              ? { top: 80 * (1 - Math.exp(-mobileRefresh.distance / 140)) }
              : undefined
          }
        >
          {banner}
          {children}
          <InlineAds />
        </div>
      </div>
      {footer && !gameDomain ? <Footer width={width} /> : null}
    </Page>
  );
}
